import type { Container } from '@/types/container'

// Use relative paths for local development, external URL for production
const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || ''

interface ApiResponse<T = any> {
  success?: boolean
  error?: string
  data?: T
  logs?: string
}

interface DeployPayload {
  name?: string
  Image?: string
  stackConfig?: string
  StackFileContent?: string
  Env?: any[]
  HostConfig?: any
  ExposedPorts?: any
  [key: string]: any
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`
  
  console.log('[API Request]', url) // Debug logging

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    })

    console.log('[API Response]', response.status, response.statusText) // Debug logging

    if (!response.ok) {
      let errorMessage = `API error: ${response.status}`
      try {
        const errorData = await response.json()
        errorMessage = errorData.error || errorData.message || errorMessage
      } catch {
        // Use default error message
      }
      throw new Error(errorMessage)
    }

    const data = await response.json()
    console.log('[API Data]', data) // Debug logging
    return data
  } catch (error) {
    console.error('[API Error]', error) // Debug logging
    if (error instanceof Error) {
      throw error
    }
    throw new Error('Network request failed')
  }
}

export async function getContainers(): Promise<Container[]> {
  const response = await apiRequest<Container[]>('/api/containers')
  return response
}

export async function deployContainer(payload: DeployPayload): Promise<ApiResponse> {
  return apiRequest<ApiResponse>('/api/deploy', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function startContainer(id: string): Promise<ApiResponse> {
  return apiRequest<ApiResponse>(`/api/containers/${id}/start`, {
    method: 'POST',
  })
}

export async function stopContainer(id: string): Promise<ApiResponse> {
  return apiRequest<ApiResponse>(`/api/containers/${id}/stop`, {
    method: 'POST',
  })
}

export async function restartContainer(id: string): Promise<ApiResponse> {
  return apiRequest<ApiResponse>(`/api/containers/${id}/restart`, {
    method: 'POST',
  })
}

export async function deleteContainer(id: string): Promise<ApiResponse> {
  return apiRequest<ApiResponse>(`/api/containers/${id}`, {
    method: 'DELETE',
  })
}

export async function getLogs(id: string): Promise<{ logs: string }> {
  return apiRequest<{ logs: string }>(`/api/containers/${id}/logs`)
}

export async function testConnection(): Promise<ApiResponse> {
  return apiRequest<ApiResponse>('/api/test-portainer')
}
